import { pluralRu } from '@wit-support/ui';
/**
 * Числа «Окупаемости» (спека §5.4). Деньги в DTO — центы USD (`*Minor`,
 * `cpp`), в доллары переводит только этот файл. Разряды — узкий неразрывный
 * пробел, минус — настоящий «−».
 */
const NNBSP = '\u202F';
const MINUS = '−';
const DASH = '—';
const int = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const two = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const one = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
function spaces(s) {
    return s.replace(/[\s\u00A0\u202F]/g, NNBSP);
}
function ok(x) {
    return typeof x === 'number' && Number.isFinite(x);
}
/** Целое с разрядами: «1 234». */
export function intRu(x) {
    return ok(x) ? spaces(int.format(x)) : DASH;
}
/** Доллары без знака: «$6 166» (|x| ≥ 100) или «$31,62». */
function dollarsAbs(major) {
    const a = Math.abs(major);
    return `$${spaces(a >= 100 ? int.format(Math.round(a)) : two.format(a))}`;
}
/** Деньги из центов: «$6 166», «$31,62», «−$1 998». `null` — «—». */
export function money(minor) {
    if (!ok(minor))
        return DASH;
    const major = minor / 100;
    const s = dollarsAbs(major);
    return major < 0 && Math.abs(major) >= 0.005 ? `${MINUS}${s}` : s;
}
/** Расход в таблицах — всегда целыми долларами: «$73», «$6 166» (без центов). */
export function moneyWhole(minor) {
    if (!ok(minor))
        return DASH;
    const major = Math.round(minor / 100);
    const s = `$${spaces(int.format(Math.abs(major)))}`;
    return major < 0 ? `${MINUS}${s}` : s;
}
/** Деньги со знаком целыми долларами (колонка «Не хватает / запас»): «+$85», «−$1 998»; ноль — «$0». */
export function signedMoneyWhole(minor) {
    if (!ok(minor))
        return DASH;
    const major = Math.round(minor / 100);
    const s = `$${spaces(int.format(Math.abs(major)))}`;
    return major < 0 ? `${MINUS}${s}` : major > 0 ? `+${s}` : s;
}
/** Деньги со знаком: «+$809», «−$1 998». */
export function signedMoney(minor) {
    if (!ok(minor))
        return DASH;
    const major = minor / 100;
    const s = dollarsAbs(major);
    return major < 0 ? `${MINUS}${s}` : `+${s}`;
}
/** Коротко: «$6,4k» от тысячи, иначе как `money`. */
export function moneyK(minor) {
    if (!ok(minor))
        return DASH;
    const major = minor / 100;
    if (Math.abs(major) < 1000)
        return money(minor);
    const s = `$${spaces(one.format(Math.abs(major) / 1000))}k`;
    return major < 0 ? `${MINUS}${s}` : s;
}
/** Два знака: «0,89». */
export function num2(x) {
    return ok(x) ? spaces(two.format(x)).replace('-', MINUS) : DASH;
}
/** «Вернётся на 1 $»: «0,89 $». */
export function roas(x) {
    return ok(x) ? `${num2(x)}${NNBSP}$` : DASH;
}
/** Доля 0…1 → «22 %»; «−0 %» не бывает — «0 %». */
export function pct(x) {
    if (!ok(x))
        return DASH;
    const r = Math.round(x * 100);
    return `${spaces(int.format(r === 0 ? 0 : r)).replace('-', MINUS)}${NNBSP}%`;
}
/** Процент уже в процентах (0…100) → «22 %». */
export function pct100(x) {
    return ok(x) ? `${spaces(int.format(x))}${NNBSP}%` : DASH;
}
/** Изменение, округлённое до нуля: без стрелки и без «дороже». */
export const NO_CHANGE = 'без изменений';
/** Изменение `a` к `b`: «▼ 21 %» / «▲ 5 %»; без базы — `null`. */
export function pctDelta(a, b) {
    if (!ok(a) || !ok(b) || b === 0)
        return null;
    const d = (a - b) / Math.abs(b);
    if (Math.round(Math.abs(d) * 100) === 0)
        return NO_CHANGE;
    const arrow = d < 0 ? '▼' : '▲';
    return `${arrow} ${spaces(int.format(Math.abs(d) * 100))}${NNBSP}%`;
}
/** Дни: «6 дн». */
export function days(x) {
    return ok(x) ? `${spaces(int.format(x))}${NNBSP}дн` : DASH;
}
// --- величины отчёта --------------------------------------------------------
/** Порог окупаемости θ — только из ответа, в UI не зашит (спека Р7). */
export function thetaOf(report) {
    return report.meta.thresholds.withFees;
}
/** Горизонт «после вычетов» (`withComponents`) или причина, почему его нет. */
export function afterFees(h) {
    const w = h.withComponents;
    if ('available' in w && w.available === false)
        return { available: false, reason: w.reason };
    if (!('roas3' in w))
        return { available: false, reason: 'нет оценки' };
    const i = w.interval3;
    return {
        available: true,
        roas: w.roas3,
        lo: i.available ? i.lo : null,
        hi: i.available ? i.hi : null,
        p: w.p3_ge_with_fees ?? null,
    };
}
/** «Не хватает / запас» в центах: `(roas3 − θ) × расход`; без оценки — `null`. */
export function gapMinor(g, theta) {
    const r = g.answer.roas3;
    return ok(r) ? (r - theta) * g.spendMinorFull : null;
}
/** «Вернётся ≈ $X ($lo–$hi)» в центах. */
export function expectedMinor(g) {
    const s = g.spendMinorFull;
    const r = g.answer.roas3;
    const lo = g.answer.interval?.lo ?? null;
    const hi = g.answer.interval?.hi ?? null;
    return {
        mid: ok(r) ? r * s : null,
        lo: ok(lo) ? lo * s : null,
        hi: ok(hi) ? hi * s : null,
    };
}
/** «Нужно» в центах: θ × расход. */
export function neededMinor(g, theta) {
    return theta * g.spendMinorFull;
}
/** «1 покупка», «342 покупки», «233 покупок» → «233 покупки»: число с окончанием. */
export function purchasesRu(n) {
    return `${intRu(n)} ${pluralRu(Math.abs(Math.round(n)), ['покупка', 'покупки', 'покупок'])}`;
}
/**
 * Доли в целых процентах, которые в сумме дают РОВНО 100 (метод наибольших
 * остатков): округляем вниз, недостающие проценты отдаём долям с самыми
 * большими остатками. Пустой набор или нулевая сумма — нули.
 */
export function largestRemainderPct(values) {
    const total = values.reduce((a, v) => a + Math.max(0, v), 0);
    if (!(total > 0))
        return values.map(() => 0);
    const raw = values.map((v) => (Math.max(0, v) / total) * 100);
    const out = raw.map(Math.floor);
    let rest = 100 - out.reduce((a, v) => a + v, 0);
    const order = raw
        .map((r, i) => ({ i, frac: r - Math.floor(r) }))
        .sort((a, b) => b.frac - a.frac || a.i - b.i);
    for (const { i } of order) {
        if (rest <= 0)
            break;
        out[i] += 1;
        rest -= 1;
    }
    return out;
}
/**
 * Целый процент доли для показа: «12 %»; ненулевая доля, округлившаяся в 0, —
 * «<1 %». Одно правило для карточек и таблиц «Кто покупает».
 */
export function sharePctText(pctInt, share) {
    if (pctInt === 0 && share > 0)
        return `<1${NNBSP}%`;
    return `${pctInt}${NNBSP}%`;
}
//# sourceMappingURL=format.js.map